import React, { useState } from 'react';
import { Users, Calendar, CheckSquare, FileText, Clock, ChevronLeft, ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
export function SubCommitteeDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const subCommitteeId = parseInt(id || '101');
  const parentCommitteeId = Math.floor(subCommitteeId / 100);
  const [activeTab, setActiveTab] = useState('overview');
  // Mock sub-committee data
  const subCommittee = {
    id: subCommitteeId,
    name: subCommitteeId === 101 ? 'Board Governance Team' : subCommitteeId === 102 ? 'Regulatory Compliance Team' : subCommitteeId === 103 ? 'Shareholder Relations Team' : 'Team',
    parentId: parentCommitteeId,
    parentName: parentCommitteeId === 1 ? 'Governance Committee' : parentCommitteeId === 2 ? 'Executive Committee' : parentCommitteeId === 3 ? 'Audit Committee' : 'Committee',
    description: 'This team is responsible for specific aspects of governance within the parent committee structure.',
    members: [{
      id: 1,
      name: 'Mohammed Al Hashimi',
      role: 'Team Lead',
      department: 'Finance',
      avatar: 'M'
    }, {
      id: 2,
      name: 'Aisha Al Mansoori',
      role: 'Coordinator',
      department: 'Finance',
      avatar: 'A'
    }, {
      id: 3,
      name: 'Omar Al Shamsi',
      role: 'Member',
      department: 'Operations',
      avatar: 'O'
    }],
    tasks: [{
      id: 1,
      title: 'Review governance framework documentation',
      status: 'completed',
      dueDate: '2023-09-15'
    }, {
      id: 2,
      title: 'Prepare quarterly compliance report',
      status: 'pending',
      dueDate: '2023-10-10'
    }, {
      id: 3,
      title: 'Conduct governance training session',
      status: 'pending',
      dueDate: '2023-09-30'
    }],
    meetings: [{
      id: 1,
      title: 'Weekly Team Sync',
      date: '2023-09-15',
      time: '10:00 AM',
      location: 'Online'
    }, {
      id: 2,
      title: 'Governance Framework Review',
      date: '2023-09-28',
      time: '02:00 PM',
      location: 'Conference Room A'
    }],
    documents: [{
      id: 1,
      name: 'Team Charter.pdf',
      type: 'PDF',
      uploadedDate: '2023-08-15'
    }, {
      id: 2,
      name: 'Q2 Progress Report.xlsx',
      type: 'XLSX',
      uploadedDate: '2023-07-20'
    }, {
      id: 3,
      name: 'Governance Framework.docx',
      type: 'DOCX',
      uploadedDate: '2023-08-30'
    }],
    stats: {
      completionRate: 75,
      meetingsHeld: 8,
      documentsUploaded: 12,
      tasksCompleted: 24
    }
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'delayed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div data-id="element-839">
      <div className="flex flex-col mb-6" data-id="element-840">
        <Link to={`/committees/${parentCommitteeId}`} className="flex items-center text-gray-600 hover:text-gray-900 mb-2" data-id="element-841">
          <ArrowLeft size={16} className="mr-1" data-id="element-842" />
          Back to {subCommittee.parentName}
        </Link>
        <div className="flex items-center" data-id="element-843">
          <Link to="/committees" className="flex items-center text-gray-600 hover:text-gray-900 mr-4" data-id="element-844">
            <ChevronLeft size={20} className="mr-1" data-id="element-845" />
            All Committees
          </Link>
          <h1 className="text-2xl font-bold" data-id="element-846">{subCommittee.name}</h1>
        </div>
        <div className="mt-1 text-sm text-gray-500" data-id="element-847">
          Sub-committee of{' '}
          <Link to={`/committees/${parentCommitteeId}`} className="text-orange-500 hover:underline" data-id="element-848">
            {subCommittee.parentName}
          </Link>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6" data-id="element-849">
        <div className="flex border-b border-gray-200" data-id="element-850">
          <button className={`px-4 py-3 font-medium ${activeTab === 'overview' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('overview')} data-id="element-851">
            Overview
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'members' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('members')} data-id="element-852">
            Members
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'tasks' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('tasks')} data-id="element-853">
            Tasks
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'meetings' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('meetings')} data-id="element-854">
            Meetings
          </button>
          <button className={`px-4 py-3 font-medium ${activeTab === 'documents' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('documents')} data-id="element-855">
            Documents
          </button>
        </div>
      </div>
      {/* Overview Tab */}
      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-856">
          {/* Sub-Committee Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-2" data-id="element-857">
            <h2 className="text-lg font-semibold mb-4" data-id="element-858">Team Information</h2>
            <p className="text-gray-700 mb-4" data-id="element-859">{subCommittee.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-id="element-860">
              <div className="bg-blue-50 p-4 rounded-lg" data-id="element-861">
                <div className="flex items-center text-blue-500 mb-2" data-id="element-862">
                  <Users size={18} className="mr-2" data-id="element-863" />
                  <span className="font-medium" data-id="element-864">Members</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-865">
                  {subCommittee.members.length}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg" data-id="element-866">
                <div className="flex items-center text-green-500 mb-2" data-id="element-867">
                  <CheckSquare size={18} className="mr-2" data-id="element-868" />
                  <span className="font-medium" data-id="element-869">Tasks</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-870">
                  {subCommittee.tasks.length}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg" data-id="element-871">
                <div className="flex items-center text-purple-500 mb-2" data-id="element-872">
                  <Calendar size={18} className="mr-2" data-id="element-873" />
                  <span className="font-medium" data-id="element-874">Meetings</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-875">
                  {subCommittee.meetings.length}
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg" data-id="element-876">
                <div className="flex items-center text-orange-500 mb-2" data-id="element-877">
                  <FileText size={18} className="mr-2" data-id="element-878" />
                  <span className="font-medium" data-id="element-879">Documents</span>
                </div>
                <p className="text-2xl font-bold" data-id="element-880">
                  {subCommittee.documents.length}
                </p>
              </div>
            </div>
            <div className="mt-6" data-id="element-881">
              <h3 className="font-medium mb-2" data-id="element-882">Team Progress</h3>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-883">
                <div className="h-2.5 rounded-full bg-orange-500" style={{
              width: `${subCommittee.stats.completionRate}%`
            }} data-id="element-884"></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500" data-id="element-885">
                <span data-id="element-886">Overall Completion</span>
                <span data-id="element-887">{subCommittee.stats.completionRate}%</span>
              </div>
            </div>
          </div>
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-888">
            <h2 className="text-lg font-semibold mb-4" data-id="element-889">Quick Actions</h2>
            <div className="space-y-3" data-id="element-890">
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-891">
                <Calendar size={18} className="mr-2" data-id="element-892" />
                Schedule Meeting
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-893">
                <CheckSquare size={18} className="mr-2" data-id="element-894" />
                Add Task
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-895">
                <FileText size={18} className="mr-2" data-id="element-896" />
                Upload Document
              </button>
              <button className="w-full flex items-center justify-center p-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-897">
                <Users size={18} className="mr-2" data-id="element-898" />
                Manage Members
              </button>
              <Link to={`/committees/${subCommittee.id}/edit`} className="w-full flex items-center justify-center p-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors" data-id="element-899">
                Edit Team Details
              </Link>
            </div>
          </div>
        </div>}
      {/* Members Tab */}
      {activeTab === 'members' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-900">
          <div className="flex justify-between items-center mb-4" data-id="element-901">
            <h2 className="text-lg font-semibold" data-id="element-902">Team Members</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-903">
              Add Member
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-904">
            <table className="w-full" data-id="element-905">
              <thead data-id="element-906">
                <tr className="bg-gray-50" data-id="element-907">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-908">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-909">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-910">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-911">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-912">
                {subCommittee.members.map(member => <tr key={member.id} data-id="element-913">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-914">
                      <div className="flex items-center" data-id="element-915">
                        <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-916">
                          {member.avatar}
                        </div>
                        <span className="font-medium" data-id="element-917">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-918">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'Team Lead' ? 'bg-blue-100 text-blue-800' : member.role === 'Coordinator' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-919">
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-920">
                      {member.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-921">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-922">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700" data-id="element-923">
                        Message
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-924">
          <div className="flex justify-between items-center mb-4" data-id="element-925">
            <h2 className="text-lg font-semibold" data-id="element-926">Tasks</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-927">
              Add Task
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-928">
            <table className="w-full" data-id="element-929">
              <thead data-id="element-930">
                <tr className="bg-gray-50" data-id="element-931">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-932">
                    Task
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-933">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-934">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-935">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-936">
                {subCommittee.tasks.map(task => <tr key={task.id} data-id="element-937">
                    <td className="px-6 py-4" data-id="element-938">
                      <div className="font-medium text-gray-900" data-id="element-939">
                        {task.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-940">
                      <div className="flex items-center" data-id="element-941">
                        <Calendar size={16} className="text-gray-500 mr-2" data-id="element-942" />
                        <span data-id="element-943">{task.dueDate}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-944">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(task.status)}`} data-id="element-945">
                        {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-946">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-947">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700" data-id="element-948">
                        Edit
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Meetings Tab */}
      {activeTab === 'meetings' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-949">
          <div className="flex justify-between items-center mb-4" data-id="element-950">
            <h2 className="text-lg font-semibold" data-id="element-951">Meetings</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-952">
              Schedule Meeting
            </button>
          </div>
          <div className="space-y-4" data-id="element-953">
            {subCommittee.meetings.map(meeting => <div key={meeting.id} className="border border-gray-200 rounded-lg p-4" data-id="element-954">
                <h3 className="font-medium mb-2" data-id="element-955">{meeting.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-956">
                  <div className="flex items-center text-sm text-gray-500" data-id="element-957">
                    <Calendar size={16} className="text-gray-500 mr-2" data-id="element-958" />
                    <span data-id="element-959">{meeting.date}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-960">
                    <Clock size={16} className="text-gray-500 mr-2" data-id="element-961" />
                    <span data-id="element-962">{meeting.time}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-963">
                    <Users size={16} className="text-gray-500 mr-2" data-id="element-964" />
                    <span data-id="element-965">{meeting.location}</span>
                  </div>
                </div>
                <div className="mt-3 flex space-x-2" data-id="element-966">
                  <button className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600 transition-colors" data-id="element-967">
                    View Details
                  </button>
                  <button className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors" data-id="element-968">
                    Edit
                  </button>
                </div>
              </div>)}
          </div>
        </div>}
      {/* Documents Tab */}
      {activeTab === 'documents' && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-969">
          <div className="flex justify-between items-center mb-4" data-id="element-970">
            <h2 className="text-lg font-semibold" data-id="element-971">Documents</h2>
            <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-972">
              Upload Document
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-973">
            <table className="w-full" data-id="element-974">
              <thead data-id="element-975">
                <tr className="bg-gray-50" data-id="element-976">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-977">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-978">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-979">
                    Uploaded Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-980">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-981">
                {subCommittee.documents.map(document => <tr key={document.id} data-id="element-982">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-983">
                      <div className="font-medium text-gray-900" data-id="element-984">
                        {document.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-985">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${document.type === 'PDF' ? 'bg-red-100 text-red-800' : document.type === 'DOCX' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-986">
                        {document.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-987">
                      {document.uploadedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-988">
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-989">
                        View
                      </button>
                      <button className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-990">
                        Download
                      </button>
                      <button className="text-red-500 hover:text-red-700" data-id="element-991">
                        Delete
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}